import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@chakra-ui/react';

import { AutoSizer, List } from 'react-virtualized';

import { TextMedium } from '../../common/text';

import {
    CategoryButton,
    CodeColumn,
    DataRow,
    DateColumn,
    Header,
    IconColumn,
    LabelColumn,
    MainContainer,
    MessageColumn,
    Content,
} from './LogsList.styles';

import { mapCategory } from './LogsList.helpers';
import { NoData } from '../NoData/NoData';

const LogsList = ({ data, headers }) => {
    const [toggled, setToggled] = useState({});
    const handleToggleCategory = (category) => {
        setToggled((prevToggled) => {
            const newToggled = {
                ...prevToggled,
                [category]: !prevToggled[category],
            };
            return newToggled;
        });
    };

    // Filter data based on toggled categories
    const filteredData = useMemo(() => {
        const isNoFilterApplied = Object.values(toggled).every((value) => !value);
        if (isNoFilterApplied) return data;

        return data.filter((row) => toggled[row.category]);
    }, [data, toggled]);

    const calculateRowHeight = (width, data) => ({ index }) => {
        const CHAR_TO_PX_RATIO = 6.5;
        const row = data[index];

        const messageLength = row.message ? row.message.length : 1;
        const charWidth = messageLength * CHAR_TO_PX_RATIO;

        const beforeMessagePx = 165;
        const beforeMessageWithLabelPx = beforeMessagePx + 100;
        const messageWidth = width - (row.label ? beforeMessageWithLabelPx : beforeMessagePx);

        const lineHeight = 20;
        const messageMinWidth = 200;

        let multiplier = Math.ceil(charWidth / messageWidth);
        if (messageWidth < messageMinWidth) {
            multiplier = Math.ceil(charWidth / width) + 1;
        }
        if (messageLength === 1) {
            multiplier = 1;
        }

        // Calculate row height based on turbineKeys length
        const turbineKeyHeight = row.turbineKeys?.length ? row.turbineKeys.length * lineHeight : 0;

        return lineHeight + lineHeight * multiplier + turbineKeyHeight;
    };

    const rowRenderer = ({ key, index, style }) => {
        const row = filteredData[index];
        return (
            <div key={key} style={{ ...style, paddingRight: 1 }}>
                <DataRow>
                    <IconColumn>{mapCategory(row.category).icon}</IconColumn>
                    <DateColumn width={85}>{row.formattedDate}</DateColumn>
                    {row.label && <LabelColumn>{row.label}</LabelColumn>}
                    {row.turbineKeys?.length > 0 && (
                        <CodeColumn width={100}>
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column', // Ensure vertical stacking
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '10px',
                                }}>
                                {row.turbineKeys.map((keyName) => (
                                    <Box key={keyName}>{keyName}</Box>
                                ))}
                            </Box>
                        </CodeColumn>
                    )}
                    {row.message && <MessageColumn autoWidth>{row.message}</MessageColumn>}
                </DataRow>
            </div>
        );
    };

    return (
        <MainContainer column>
            <Header>
                {headers.map((header) => (
                    <CategoryButton
                        key={header.category}
                        inActive={toggled[header.category]}
                        category={header.category}
                        onClick={() => handleToggleCategory(header.category)}
                        title={header.label}>
                        <TextMedium className="header">{header.label}</TextMedium>
                        <TextMedium>
                            {header.icon}
                            {header.count}
                        </TextMedium>
                    </CategoryButton>
                ))}
            </Header>
            <Content>
                <AutoSizer defaultWidth={100} defaultHeight={100}>
                    {({ height, width }) => (
                        <List
                            height={height}
                            width={width}
                            noRowsRenderer={() => <NoData />}
                            rowHeight={calculateRowHeight(width, filteredData)}
                            rowCount={filteredData.length}
                            rowRenderer={rowRenderer}
                        />
                    )}
                </AutoSizer>
            </Content>
        </MainContainer>
    );
};

LogsList.propTypes = {
    headers: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
};

export default LogsList;
